import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const organisationSharingCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'OrganisationSharing',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.organisationSharings.`,
});

export async function getOrganisationSharings(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = organisationSharingCache.getFromMemory('all');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('ORGANISATIONS_SHARING');
            if (legacyData) {
                log('info', 'OrganisationSharing', '📦 Seeding Vue cache from legacy storage (ORGANISATIONS_SHARING)');
                await organisationSharingCache.put('all', legacyData);
                organisationSharingCache.setInMemory('all', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'OrganisationSharing', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await organisationSharingCache.get('all');
        if (cached && !forceRefresh) {
            organisationSharingCache.setInMemory('all', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'OrganisationSharing', '📴 Offline: Returning cached organisation sharings');
                return cached;
            }

            setTimeout(() => refreshOrganisationSharingsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'OrganisationSharing', '📴 Offline: No cached organisation sharings available');
            return [];
        }

        return await fetchOrganisationSharingsFromAPI();
    } catch (error) {
        log('error', 'OrganisationSharing', `❌ Error getting organisation sharings: ${error.message}`);

        try {
            const fallback = await organisationSharingCache.get('all');
            if (fallback) {
                log('info', 'OrganisationSharing', '💾 Using fallback cached organisation sharings');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'OrganisationSharing', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

async function fetchOrganisationSharingsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Core/OrganisationSharings'), { method: 'GET' }, 'token');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const organisationSharings = await response.json();
        await organisationSharingCache.put('all', organisationSharings);
        organisationSharingCache.setInMemory('all', organisationSharings);

        log('success', 'OrganisationSharing', `✅ Fetched and cached ${organisationSharings.length} organisation sharings`);
        return organisationSharings;
    } catch (error) {
        log('error', 'OrganisationSharing', `❌ Failed to fetch organisation sharings from API: ${error.message}`);
        throw error;
    }
}

async function refreshOrganisationSharingsInBackground() {
    try {
        await fetchOrganisationSharingsFromAPI();
        log('success', 'OrganisationSharing', '🔄 Background refresh of organisation sharings completed');
    } catch (error) {
        log('warn', 'OrganisationSharing', `⚠️ Background refresh failed: ${error.message}`);
    }
}

export async function refreshOrganisationSharings() {
    organisationSharingCache.clearMemory();
    await organisationSharingCache.clear();
}
